import {parseGradient} from "../../components/editor/GradientPicker";

export const BackgroundType = {
    Image: 'image', Gradient: 'gradient', Color: 'color',
}

export const PROPERTY_IMAGE = 'background-image';
export const PROPERTY_BG_TYPE = '__background-type';
export const PROPERTY_BG_IMAGE = PROPERTY_IMAGE;

const getColorAsGradient = (color) => `linear-gradient(${color} 1%, ${color} 100%)`;
const getLayerFromBgImage = (imagePart) => {
    const result = {
        [PROPERTY_BG_IMAGE]: imagePart,
    };

    if (imagePart.indexOf('url(') > -1) {
        result[PROPERTY_BG_TYPE] = BackgroundType.Image;
    } else if (imagePart.indexOf('gradient(') > -1) {
        const parsed = parseGradient(imagePart);
        const stops = parsed.colorStops;
        const isLinear = parsed.name.indexOf('linear') >= 0;

        if (stops.length === 2 && isLinear && (stops[0].color === stops[1].color)) {
            result[PROPERTY_BG_TYPE] = BackgroundType.Color;
        } else {
            result[PROPERTY_BG_TYPE] = BackgroundType.Gradient;
        }
    } else {
        result[PROPERTY_BG_TYPE] = BackgroundType.Color;
        result[PROPERTY_BG_IMAGE] = getColorAsGradient(imagePart);
    }

    return result;
};

export const getStyleManagerProperties = () => {
    return {
        sectors: [{
            id: 'size',
            name: 'Size',
            properties: [{extend: 'width', units: ["px"]}, {extend: 'height', units: ["px"]},],

        }, {
            id: 'space',
            name: 'Space',
            properties: [
                {
                    // extend: 'padding',
                    id: 'padding',
                    name: 'Padding',
                    type: 'composite',
                    detached: true,
                    properties: ["padding-top", "padding-right", "padding-bottom", "padding-left"].map(prop => ({
                        type: 'integer', property: prop, default: '0px', units: ["px"]
                        // extend: prop, units: ["px"]
                    }))
                },
                {
                    extend: 'margin',
                    type: 'composite',
                    detached: true,
                    properties: ["margin-top", "margin-right", "margin-bottom", "margin-left"].map(prop => ({
                        type: 'integer', property: prop, default: '0px', units: ["px"]
                    }))
                },
            ]
        }, {
            id: 'position',
            name: 'Position',
            properties: [
                {
                    extend: 'position',
                    options: [
                        {id: 'static', label: 'Static'},
                        {id: 'relative', label: 'Relative'},
                        {id: 'absolute', label: 'Absolute'},
                        {id: 'fixed', label: 'Fixed'},
                        {id: 'sticky', label: 'Sticky'},
                    ],
                    onChange({opts, property}) {
                        if (opts.__clear) {
                            const clearPositionProps = {top: '', right: '', bottom: '', left: '', 'z-index': ''};
                            property.__upTargetsStyle(clearPositionProps, opts);
                        }
                    }
                },
                {extend: 'top', units: ['px', '%']},
                {extend: 'right', units: ['px', '%']},
                {extend: 'bottom', units: ['px', '%']},
                {extend: 'left', units: ['px', '%']},
                {
                    extend: 'z-index',
                    type: 'integer',
                    default: '0',
                },
            ],
        }, {
            id: 'typography',
            name: 'Typography',
            properties: [{
                Name: 'Font',
                extend: 'font-family',
                options: [{value: '"Gilroy", "Noto Sans KR", sans-serif', name: 'Noto Sans KR'},
                    {value: '"Noto Sans KR", sans-serif', name: 'Noto Sans KR(기호)'},
                    {
                        value: '"Gilroy", sans-serif',
                        name: 'Gilroy'
                    }, {value: '"Malgun Gothic"', name: '맑은 고딕'}, {value: '"Dotum"', name: '돋움'}]
            }, {Name: 'Color', extend: 'color'}, {
                Name: 'Size', extend: 'font-size', units: ["px"]
            }, {
                Name: 'Weight',
                extend: 'font-weight'
            }, {
                Name: 'Letter Spacing', property: 'letter-spacing', units: ["px"],
                type: 'slider', defaults: '0', min: -5, max: 5, step: 0.1
            }, {
                Name: 'Line-height', property: 'line-height', units: [''],
                type: 'slider', defaults: '1', min: 0, max: 5, step: 0.1
            }, {Name: 'Align', extend: 'text-align'}, {
                name: 'Decoration',
                extend: 'text-decoration',
                type: 'select',
                default: 'none',
                options: [{id: 'none', label: 'None'}, {id: 'underline', label: 'Underline'}, {
                    id: 'line-through',
                    label: 'Line through'
                },],
            }],
        }, {
            id: 'background',
            name: 'Background',
            properties: [// Stack of backgrounds (Color - Gradient - Image/Patterns)
                {
                    property: 'background',
                    type: 'stack',
                    layerSeparator: /(?<!\(.*[^)]),(?![^(]*\))/,
                    layerJoin: ', ',
                    detached: true,
                    layerLabel: (l, {values, property}) => {
                        return property.getProperty(PROPERTY_BG_TYPE).getOptionLabel(values[PROPERTY_BG_TYPE]);
                    },
                    toStyle(values, {name}) {
                        const type = values[PROPERTY_BG_TYPE];
                        let image = values[PROPERTY_BG_IMAGE];

                        if (type === 'color' && image.indexOf('linear-gradient') < 0) {
                            image = `linear-gradient(${image} 1%, ${image} 100%)`;
                        }

                        const result = {
                            ...values, [PROPERTY_BG_IMAGE]: image,
                        };

                        return result;
                    },
                    fromStyle(style, {property, name, separatorLayers}) {
                        const sep = separatorLayers;
                        const props = property.getProperties();
                        let layers = [];

                        if (style[PROPERTY_IMAGE]) {
                            // Get layers from the `background-image` property
                            layers = property.__splitStyleName(style, PROPERTY_IMAGE, sep).map(getLayerFromBgImage);

                            // Update layers by inner properties
                            props.forEach((prop) => {
                                const id = prop.getId();
                                const propName = prop.getName();
                                if (propName === PROPERTY_IMAGE) return;
                                property.__splitStyleName(style, propName, sep)
                                    .map((value) => ({[id]: value || prop.getDefaultValue()}))
                                    .forEach((inLayer, i) => {
                                        layers[i] = layers[i] ? {...layers[i], ...inLayer} : inLayer;
                                    });
                            });
                        } else if (style[name]) {
                            // Partial support for the `background` property
                            // eslint-disable-next-line
                            layers = property.__splitStyleName(style, name, /(?![^)(]*\([^)(]*?\)\)),(?![^\(]*\))/)
                                .map((value) => value.substring(0, value.lastIndexOf(')') + 1))
                                .map(getLayerFromBgImage);
                        }

                        return layers;
                    },
                    properties: [{
                        property: PROPERTY_BG_TYPE,
                        type: 'radio',
                        default: 'image',
                        options: [{id: BackgroundType.Image}, {id: BackgroundType.Gradient}, {id: BackgroundType.Color},]
                    }, {
                        // Specs: https://www.w3.org/TR/css-images-3/
                        // <url> | <gradient> (https://github.com/rafaelcaricio/gradient-parser)
                        // <gradient> = <linear-gradient()> | <repeating-linear-gradient()> | <radial-gradient()> | <repeating-radial-gradient()>
                        // <linear-gradient()> = linear-gradient([ <angle> | to <side-or-corner> ]? , <color-stop-list>)
                        // <radial-gradient()> = radial-gradient([ <ending-shape> || <size> ]? [ at <position> ]? , <color-stop-list>
                        //    radial-gradient(5em circle at top left, yellow, blue)
                        // <color-stop-list> = <linear-color-stop> , [ <linear-color-hint>? , <linear-color-stop> ]
                        //  fn(red 40%, white) | fn(white calc(-25px + 50%), blue)
                        // Regex
                        // <side-or-corner>: /^to (left (top|bottom)|right (top|bottom)|top (left|right)|bottom (left|right)|left|right|top|bottom)/i,
                        // <ending-shape>: /^circle|ellipse/i,
                        // <size>: /^(closest\-side|closest\-corner|farthest\-side|farthest\-corner|contain|cover)/, // | <length> | <length-percentage>{2}
                        // <position>: /^at/ // position: at top left, at 20px, at 10% 15%
                        property: PROPERTY_BG_IMAGE, default: 'none', // url(), gradient, none
                    }, {
                        property: 'background-position',
                        type: 'composite', // default: '0%', // left | center | right | top | bottom
                        properties: ['background-position-x', 'background-position-y',].map(prop => ({
                            type: 'integer', property: prop, units: ['px'], default: '0px',
                        }))
                    }, {
                        // Keyword: contain | cover; One-value syntax; Two-value syntax
                        property: 'background-size',
                        type: 'composite',
                        default: 'cover',
                        properties: ['background-size-x', 'background-size-y',].map(prop => ({
                            type: 'integer', property: prop, units: ['px'], default: '100%',
                        }))
                    }, {
                        property: 'background-repeat', // box (x, y)
                        default: 'no-repeat', type: 'select', options: [ // background-repeat-x background-repeat-x
                            {id: 'repeat', label: 'Repeat'}, {id: 'repeat-x', label: 'Repeat x'}, {
                                id: 'repeat-y',
                                label: 'Repeat y'
                            }, {id: 'no-repeat', label: 'No repeat'}, {id: 'space', label: 'Space'}, {
                                id: 'round',
                                label: 'Round'
                            },]
                    }, {
                        property: 'background-attachment',
                        default: 'scroll',
                        type: 'select',
                        options: [{id: 'scroll', label: 'Scroll'}, {id: 'fixed', label: 'Fixed'},]
                    }, {
                        property: 'background-origin',
                        default: 'padding-box',
                        type: 'select',
                        options: [{id: 'padding-box', label: 'Padding box'}, {
                            id: 'border-box',
                            label: 'Border box'
                        }, {id: 'content-box', label: 'Content box'},]
                    },]
                }, {
                    property: 'background-color', type: 'color', default: 'none',
                }],
        }, {
            id: 'borders', name: 'Borders', properties: [{
                extend: 'border-radius',
                type: 'composite',
                detached: true,
                properties: ["border-top-left-radius", "border-top-right-radius", "border-bottom-left-radius", "border-bottom-right-radius"].map(prop => ({
                    type: 'integer', property: prop, default: '0', units: ['px'],
                })),
            }, {
                extend: 'border-width',
                type: 'composite',
                detached: true,
                properties: ['border-top-width', 'border-right-width', 'border-bottom-width', 'border-left-width',].map(prop => ({
                    type: 'integer', property: prop, default: '0', units: ['px'],
                }))
            }, {
                extend: 'border-style',
                type: 'composite',
                detached: true,
                properties: ['border-top-style', 'border-right-style', 'border-bottom-style', 'border-left-style',].map(prop => ({
                    type: 'select',
                    property: prop,
                    default: 'none',
                    options: [{id: 'none', label: 'None'}, {id: 'solid', label: 'Solid'}, {
                        id: 'dotted',
                        label: 'Dotted'
                    }, {id: 'dashed', label: 'Dashed'}, {id: 'double', label: 'Double'}, {
                        id: 'groove',
                        label: 'Groove'
                    }, {id: 'ridge', label: 'Ridge'}, {id: 'inset', label: 'Inset'}, {id: 'outset', label: 'Outset'},]
                }))
            }, {
                extend: 'border-color',
                type: 'composite',
                detached: true,
                properties: ['border-top-color', 'border-right-color', 'border-bottom-color', 'border-left-color',].map(prop => ({
                    type: 'color', property: prop, default: 'currentColor',
                }))
            },],
        },],
    }
}